/**
 * Subscription Service
 *
 * This service handles all subscription-related API calls including
 * plan management, billing, and Stripe integration.
 *
 * NOTE: All subscriptions are organization-based. Each organization has one subscription
 * that covers all users in that organization. The API automatically handles the organization
 * context based on the authenticated user's token.
 */

import { VITE_CORE_API_URL } from "@/constants/env";
import type {
	BillingAddress,
	Invoice,
	PaymentMethod,
	SubscriptionChangePreview,
	SubscriptionPlan,
	SubscriptionUsage,
	UserSubscription,
} from "@/models/subscription/subscription.model";
import { auth } from "@/services/auth-service";

/**
 * Get all available subscription plans
 */
export const getSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/plans`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			throw new Error("Failed to fetch subscription plans");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to fetch subscription plans:", error);
		throw new Error("Failed to fetch subscription plans");
	}
};

/**
 * Get current organization's subscription details
 * Returns subscription data or special object indicating exemption
 */
export const getCurrentSubscription =
	async (): Promise<UserSubscription | null> => {
		try {
			const response = await fetch(
				`${VITE_CORE_API_URL}/core/subscription/current`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					},
				},
			);

			if (response.status === 404) {
				// Check if the error indicates an exemption or lack of subscription
				const errorData = await response.json();
				if (errorData.message?.includes("Trial user")) {
					throw errorData;
				}
				return null; // No subscription found (and no exemption)
			}

			if (!response.ok) {
				throw new Error("Failed to fetch current subscription");
			}

			const result = await response.json();

			// Check if organization is exempt from subscription requirements
			if (result.data?.isExempt === true) {
				// Return a special subscription-like object indicating exemption
				// This allows the organization to use the system without an actual subscription
				return {
					isExempt: true,
					status: "active",
					exemption: result.data.exemption,
				} as any;
			}

			return result.data;
		} catch (error) {
			console.error("Failed to fetch current subscription:", error);
			throw error;
		}
	};

/**
 * Create a new subscription
 */
export const createSubscription = async (
	planId: string,
	paymentMethodId?: string,
): Promise<{ clientSecret?: string; subscription: UserSubscription }> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/create`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					planId,
					paymentMethodId,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to create subscription");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to create subscription:", error);
		throw error;
	}
};

/**
 * Update subscription plan
 */
export const updateSubscriptionPlan = async (
	newPlanId: string,
): Promise<UserSubscription> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/update-plan`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					planId: newPlanId,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to update subscription");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to update subscription:", error);
		throw error;
	}
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (
	cancelAtPeriodEnd: boolean = true,
): Promise<UserSubscription> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/cancel`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					cancelAtPeriodEnd,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to cancel subscription");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to cancel subscription:", error);
		throw error;
	}
};

/**
 * Reactivate a canceled subscription
 */
export const reactivateSubscription = async (): Promise<UserSubscription> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/reactivate`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to reactivate subscription");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to reactivate subscription:", error);
		throw error;
	}
};

/**
 * Get payment methods
 */
export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/payment-methods`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			throw new Error("Failed to fetch payment methods");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to fetch payment methods:", error);
		throw new Error("Failed to fetch payment methods");
	}
};

/**
 * Add a new payment method
 */
export const addPaymentMethod = async (
	paymentMethodId: string,
	setAsDefault: boolean = false,
): Promise<PaymentMethod> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/payment-methods`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					paymentMethodId,
					setAsDefault,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to add payment method");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to add payment method:", error);
		throw error;
	}
};

/**
 * Remove a payment method
 */
export const removePaymentMethod = async (
	paymentMethodId: string,
): Promise<void> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/payment-methods/${paymentMethodId}`,
			{
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to remove payment method");
		}
	} catch (error) {
		console.error("Failed to remove payment method:", error);
		throw error;
	}
};

/**
 * Get billing history/invoices
 */
export const getInvoices = async (): Promise<Invoice[]> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/invoices`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			throw new Error("Failed to fetch invoices");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to fetch invoices:", error);
		throw new Error("Failed to fetch invoices");
	}
};

/**
 * Get subscription usage statistics
 */
export const getSubscriptionUsage = async (): Promise<SubscriptionUsage> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/usage`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			throw new Error("Failed to fetch subscription usage");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to fetch subscription usage:", error);
		throw new Error("Failed to fetch subscription usage");
	}
};

/**
 * Preview subscription change
 */
export const previewSubscriptionChange = async (
	newPlanId: string,
): Promise<SubscriptionChangePreview> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/preview-change`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					planId: newPlanId,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				errorData.message || "Failed to preview subscription change",
			);
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to preview subscription change:", error);
		throw error;
	}
};

/**
 * Update billing address
 */
export const updateBillingAddress = async (
	address: BillingAddress,
): Promise<void> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/billing-address`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify(address),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to update billing address");
		}
	} catch (error) {
		console.error("Failed to update billing address:", error);
		throw error;
	}
};

/**
 * Update subscription's default payment method
 */
export const updateSubscriptionPaymentMethod = async (
	subscriptionId: string,
	paymentMethodId: string,
): Promise<void> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/${subscriptionId}/payment-method`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
				body: JSON.stringify({
					paymentMethodId,
				}),
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				errorData.message || "Failed to update subscription payment method",
			);
		}
	} catch (error) {
		console.error("Failed to update subscription payment method:", error);
		throw error;
	}
};

/**
 * Get location count and pricing information for per-location billing
 *
 * This function fetches the user's organization location count and calculates
 * the total monthly cost based on $25 per location pricing.
 *
 * Used for displaying: "$25 × X locations = $Total/month"
 *
 * @returns Promise with location count and pricing calculations
 */
export const getLocationPricing = async (): Promise<{
	locationCount: number;
	basePrice: number;
	totalPrice: number;
	formattedBasePrice: string;
	formattedTotalPrice: string;
}> => {
	try {
		const response = await fetch(
			`${VITE_CORE_API_URL}/core/subscription/location-count`,
			{
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			},
		);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to get location pricing");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to get location pricing:", error);
		throw error;
	}
};
